import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { ModalHeader, ModalBody, ModalFooter, Button, Input, Label } from 'reactstrap';
import Rodal from '../../../../components/Rodal/Rodal';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import createNotification from '../../../../utils/createNotification';
import { MarketplaceBuyer } from '../../../../slices/marketplace/buyer/models/buyer';
import { LinkBuyerByAdmin } from '../../../../slices/marketplace/timeline/thunks/link-buyer-by-admin.thunk';
import { BuyerLinkOutcome } from '../../../../slices/marketplace/timeline/models/enums/buyer-link-outcome.enum';
import { EngagementBuyer } from '../../../../slices/marketplace/timeline/models/engagement-buyer.model';
import { MarketplaceOpportunityStatus } from '../../../../slices/marketplace/opportunity/models/opportunity-status';
import './LinkBuyerDialog.scss';

interface LinkBuyerDialogProps {
    isVisible: boolean;
    engagementId: number;
    buyers: MarketplaceBuyer[];
    onClose: () => void;
    onLinked: () => Promise<any> | Promise<void> | void;
}

const SUCCESS_OUTCOMES = [BuyerLinkOutcome.Linked, BuyerLinkOutcome.ReEngaged];

// [#1314 review] A buyer in the deal on the engagement is refused a link (AlreadyActive), so they are not offered; a
// buyer who left - rejected, declined, a Lost Opportunity - can be brought back and stays in the list.
export const linkableBuyers = (
    marketplaceBuyers: MarketplaceBuyer[],
    engagementBuyers: EngagementBuyer[]
): MarketplaceBuyer[] => {
    const inTheDeal = new Set(
        engagementBuyers
            .filter(
                (buyer) =>
                    buyer.isActive &&
                    !buyer.actionState?.some(
                        (state) =>
                            state.buyerStatus === MarketplaceOpportunityStatus.LostOpportunity
                    )
            )
            .map((buyer) => buyer.buyerId)
    );
    return marketplaceBuyers.filter((buyer) => !inTheDeal.has(buyer.id));
};

export const formatLinkBuyerOption = (buyer: MarketplaceBuyer): string => {
    const company = [buyer.corporateAffiliationName, buyer.buyerAcronym]
        .filter((part) => part && part.trim())
        .join(', ');
    return `${company} — ${buyer.userFullName}`;
};

const LinkBuyerDialog = (props: LinkBuyerDialogProps) => {
    const dispatch = useDispatch<any>();
    const [selectedBuyer, setSelectedBuyer] = useState<MarketplaceBuyer>(null);
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
    const [isLinking, setIsLinking] = useState(false);

    if (!props.isVisible) {
        return null;
    }

    const linkSelectedBuyer = async () => {
        setIsLinking(true);
        try {
            const result = await dispatch(
                LinkBuyerByAdmin({
                    engagementId: props.engagementId,
                    buyerId: selectedBuyer.id,
                })
            ).unwrap();
            createNotification(
                result.message,
                SUCCESS_OUTCOMES.includes(result.outcome) ? 'success' : 'info'
            );
            setSelectedBuyer(null);
            setIsConfirmationVisible(false);
            props.onClose();
            await props.onLinked();
        } catch {
            setIsConfirmationVisible(false);
        } finally {
            setIsLinking(false);
        }
    };

    return (
        <>
            <Rodal
                visible={true}
                animation="slideTop"
                width={498}
                showCloseButton={false}
                onClose={props.onClose}
                className="confirmation-modal link-buyer-dialog">
                <ModalHeader>Link Buyer</ModalHeader>
                <ModalBody>
                    <div className="link-buyer-field">
                        <Label for="linkBuyer">Buyer</Label>
                        <Input
                            type="select"
                            id="linkBuyer"
                            name="linkBuyer"
                            value={selectedBuyer?.id ?? ''}
                            onChange={(event) =>
                                setSelectedBuyer(
                                    props.buyers.find(
                                        (buyer) =>
                                            buyer.id === Number(event.target.value)
                                    ) ?? null
                                )
                            }>
                            <option value="">Select</option>
                            {props.buyers.map((buyer) => (
                                <option key={buyer.id} value={buyer.id}>
                                    {formatLinkBuyerOption(buyer)}
                                </option>
                            ))}
                        </Input>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        type="button"
                        className="btn-cancel"
                        onClick={props.onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        className="btn btn-primary"
                        disabled={!selectedBuyer || isLinking}
                        onClick={() => setIsConfirmationVisible(true)}>
                        Link Buyer
                    </Button>
                </ModalFooter>
            </Rodal>
            {isConfirmationVisible && selectedBuyer && (
                <ConfirmationModal
                    isVisible={isConfirmationVisible}
                    setIsVisible={setIsConfirmationVisible}
                    header="Link Buyer Confirmation"
                    confirmationText={`Are you sure you want to link ${formatLinkBuyerOption(
                        selectedBuyer
                    )} to this engagement?`}
                    onConfirm={linkSelectedBuyer}
                    closeOnConfirm={false}
                    isLoading={isLinking}
                    params={''}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel & Close"
                />
            )}
        </>
    );
};

export default LinkBuyerDialog;
